import { useEffect, useRef } from 'react';
import type { RocketRecord } from '@trajectory/contracts';
import { apiFetch } from '@/lib/api';
import { useMissionStore } from '@/stores/missionStore';
import { loadRocketRecord, useRocketStore } from '@/stores/rocketStore';
import { useUiStore } from '@/stores/uiStore';

/**
 * Keeps the Rocket Editor showing whatever rocket the open mission actually
 * points at. Mounted once at the shell root rather than inside Rocket Editor
 * itself, so switching missions updates the rocket even while that panel is
 * closed.
 *
 * It reacts to changes in the *mission's* `rocketId` only, tracked in a ref.
 * Keying off the loaded rocket too — as this used to — meant any divergence
 * between the two re-imposed the mission's rocket: with a mission open you
 * could not open a different rocket to compare it, and returning the editor to
 * its picker snapped straight back to the mission's rocket.
 */
export function useSyncRocketWithMission(): void {
  const rocketId = useMissionStore((state) => state.document?.rocketId ?? null);
  const syncedRocketId = useRef<string | null>(null);

  useEffect(() => {
    // No rocketId doesn't mean "clear the editor" — the user may be editing a
    // rocket independently of any mission, and switching to a rocket-less
    // mission shouldn't stomp on that.
    if (!rocketId) {
      syncedRocketId.current = null;
      return undefined;
    }
    if (rocketId === syncedRocketId.current) return undefined;
    syncedRocketId.current = rocketId;

    if (useRocketStore.getState().id === rocketId) return undefined;

    let cancelled = false;
    apiFetch<RocketRecord>(`/rockets/${rocketId}`)
      .then((record) => {
        if (!cancelled) loadRocketRecord(record);
      })
      .catch((error) => {
        // The mission's rocketId may point at a deleted rocket; leave the
        // editor empty rather than crash the shell over it.
        if (cancelled) return;
        console.warn(`Could not load rocket "${rocketId}" referenced by the mission.`, error);
        useUiStore.getState().notify({
          kind: 'warning',
          title: 'Mission rocket is unavailable',
          message:
            error instanceof Error
              ? error.message
              : `The rocket "${rocketId}" could not be loaded. Link another vehicle in Rocket Editor.`,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [rocketId]);
}
